// Breakdance Agent Types
export enum MoveCategory {
  TopRock = 0,
  Footwork = 1,
  PowerMoves = 2,
  Freezes = 3,
  Transitions = 4,
  Floorwork = 5,
  Tricks = 6
}

export enum MediaType {
  StillImage = 0,
  ActionShot = 1,
  VideoFrame = 2,
  SequentialFrames = 3
}

export enum DetailLevel {
  Basic = 0,
  Moderate = 1,
  High = 2,
  Expert = 3
}

export interface BreakdancePromptRequest {
  baseDescription?: string;
  moveCategory?: MoveCategory;
  specificMove?: string;
  mediaType?: MediaType;
  anatomicalDetail?: DetailLevel;
  physicsDetail?: DetailLevel;
  movementSpeed?: string;
  powerLevel?: string;
}

export interface BreakdanceSession {
  sessionId: string;
  currentPrompt: string;
  iterations: BreakdanceIteration[];
  metadata?: Record<string, any>;
}

export interface BreakdanceIteration {
  iterationNumber: number;
  generatedPrompt: string;
  enhancementFocus?: string;
  timestamp: string;
}

// Disco Culture Agent Types
export enum DiscoEra {
  EarlyDisco = 0,
  ClassicDisco = 1,
  LateDisco = 2
}

export enum DiscoMediaType {
  DanceFloor = 0,
  NightclubScene = 1,
  Fashion = 2,
  Lifestyle = 3
}

export enum SocioeconomicLevel {
  WorkingClass = 0,
  MiddleClass = 1,
  UpperClass = 2,
  Mixed = 3
}

export interface DiscoCulturePromptRequest {
  baseDescription?: string;
  discoEra?: DiscoEra;
  mediaType?: DiscoMediaType;
  city?: string;
  neighborhood?: string;
  demographics?: string;
  socioeconomicLevel?: SocioeconomicLevel;
  includeAuthenticDemographics?: boolean;
}

export interface DiscoCultureSession {
  sessionId: string;
  currentPrompt: string;
  iterations: DiscoCultureIteration[];
  metadata?: Record<string, any>;
}

export interface DiscoCultureIteration {
  iterationNumber: number;
  generatedPrompt: string;
  culturalContext?: string;
  timestamp: string;
}

// Stock Photo Technology Agent Types
export enum TechnologyFocus {
  AI = 0,
  CloudComputing = 1,
  Cybersecurity = 2,
  DataScience = 3,
  IoT = 4,
  Blockchain = 5,
  QuantumComputing = 6,
  AR_VR = 7,
  Robotics = 8,
  FiveTech = 9,
  EdgeComputing = 10,
  MachineLearning = 11,
  DevOps = 12,
  Mobile = 13
}

export enum TechnologySubsector {
  Enterprise = 0,
  Consumer = 1,
  Healthcare = 2,
  Finance = 3,
  Education = 4,
  Manufacturing = 5
}

export enum StockPhotoStyle {
  Corporate = 0,
  Modern = 1,
  Futuristic = 2,
  Minimalist = 3,
  Documentary = 4
}

export enum UsageType {
  Editorial = 0,
  Commercial = 1,
  Marketing = 2,
  Educational = 3
}

export enum QualityLevel {
  Standard = 0,
  Premium = 1,
  UltraHigh = 2
}

export enum LightingMood {
  Bright = 0,
  Moody = 1,
  Natural = 2,
  Dramatic = 3,
  Clinical = 4
}

export enum Orientation {
  Landscape = 0,
  Portrait = 1,
  Square = 2,
  Panoramic = 3
}

export interface StockPhotoTechnologyPromptRequest {
  baseDescription?: string;
  technologyFocus?: TechnologyFocus;
  technologySubsectors?: TechnologySubsector[];
  stockPhotoStyle?: StockPhotoStyle;
  usageType?: UsageType;
  qualityLevel?: QualityLevel;
  lightingMood?: LightingMood;
  orientation?: Orientation;
}

export interface StockPhotoTechnologySession {
  sessionId: string;
  currentPrompt: string;
  iterations: StockPhotoTechnologyIteration[];
  metadata?: Record<string, any>;
}

export interface StockPhotoTechnologyIteration {
  iterationNumber: number;
  generatedPrompt: string;
  technicalDetails?: string;
  timestamp: string;
}

// Urban Bike Auto Life Agent Types
export enum UrbanCultureType {
  BikeLife = 0,
  AutoCulture = 1,
  StreetRacing = 2,
  Lowriders = 3
}

export enum UrbanMediaType {
  ActionShot = 0,
  GroupPhoto = 1,
  DetailShot = 2,
  SceneShot = 3
}

export interface UrbanCulturePromptRequest {
  baseDescription?: string;
  cultureType?: UrbanCultureType;
  mediaType?: UrbanMediaType;
  city?: string;
  neighborhood?: string;
  era?: string;
  demographics?: string;
  includeSocioeconomicContext?: boolean;
}

export interface UrbanBikeAutoLifeSession {
  sessionId: string;
  currentPrompt: string;
  iterations: UrbanBikeAutoLifeIteration[];
  metadata?: Record<string, any>;
}

export interface UrbanBikeAutoLifeIteration {
  iterationNumber: number;
  generatedPrompt: string;
  culturalElements?: string;
  timestamp: string;
}

// Common types for enhancement requests
export interface EnhanceRequest {
  enhancementFocus?: string;
  temperature?: number;
  maxTokens?: number;
}

export interface EnhanceDiscoRequest extends EnhanceRequest {
  additionalContext?: string;
}

export interface StockPhotoEnhancementRequest extends EnhanceRequest {
  technicalFocus?: string;
}

export interface EnhanceUrbanRequest extends EnhanceRequest {
  culturalDepth?: string;
}

// Agent configurations
export interface NewNicheAgentConfig {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  endpoint: string;
  category: string;
}

export const NEW_NICHE_AGENTS: NewNicheAgentConfig[] = [
  {
    id: 'breakdance',
    name: 'Breakdance Movement Visualizer',
    description: 'Generate dynamic breakdance and b-boy/b-girl movement visualizations',
    icon: '🕺',
    color: '#FF5722',
    endpoint: '/api/agents/breakdance',
    category: 'Urban Arts & Movement'
  },
  {
    id: 'culture-disco',
    name: 'Disco Culture Scene Generator',
    description: 'Create authentic disco era scenes with cultural and social context',
    icon: '🪩',
    color: '#9C27B0',
    endpoint: '/api/agents/culture-disco',
    category: 'Cultural History'
  },
  {
    id: 'stock-photo-technology',
    name: 'Tech Stock Photo Creator',
    description: 'Generate professional technology-focused stock photography',
    icon: '💻',
    color: '#2196F3',
    endpoint: '/api/agents/stock-photo-technology',
    category: 'Professional Photography'
  },
  {
    id: 'urban-bike-auto-life',
    name: 'Urban Bike & Auto Culture',
    description: 'Visualize urban bike life, car culture, and street racing scenes',
    icon: '🏍️',
    color: '#795548',
    endpoint: '/api/agents/urban-bike-auto-life',
    category: 'Urban Culture'
  }
];